// client/src/lib/session.js

/**
 * Shared session helpers for the school-admin, teacher and student portals.
 *
 * The three portals share the /dashboard route, so they must agree on how a
 * session starts and ends. Previously each rolled its own: one cleared all of
 * localStorage, one cleared a single redux slice, and none told the server -
 * which is how a "logged out" browser kept working.
 */

import axios from 'axios';
import { API_URL } from '@/variables';
import { resetAllAuth } from '@/store/authActions';

export const PORTAL_ROLES = ['admin', 'teacher', 'student'];

/**
 * Ask the server which portal this browser is signed in to.
 * Returns the session payload, or null when there is no valid session.
 */
export const fetchSession = async () => {
    try {
        const { data } = await axios.get(`${API_URL}/app/verify`);
        return data?.success ? data : null;
    } catch (error) {
        return null;
    }
};

/**
 * Page-lifetime cache of the verify result.
 *
 * The dashboard layout and the page it wraps both need the session, and every
 * client-side navigation would otherwise re-request it. A session that expires
 * mid-visit is still caught by the 401 interceptor in lib/axiosConfig.
 */
let cachedSession;      // undefined = not fetched yet, null = no session
let inflightRequest = null;

export const loadSessionOnce = () => {
    if (cachedSession !== undefined) return Promise.resolve(cachedSession);

    if (!inflightRequest) {
        inflightRequest = fetchSession().then((session) => {
            cachedSession = session;
            inflightRequest = null;
            return session;
        });
    }

    return inflightRequest;
};

/** Drop the cache so the next read re-checks with the server. */
export const invalidateSession = () => {
    cachedSession = undefined;
    inflightRequest = null;
};

/**
 * Clear locally held session state. Kept separate from the server call so a
 * failed network request can never leave the browser looking logged in.
 */
export const clearLocalSession = (dispatch) => {
    invalidateSession();

    if (dispatch) dispatch(resetAllAuth());

    if (typeof window !== 'undefined') {
        localStorage.removeItem('state');
        localStorage.removeItem('userRole');
    }
};

/**
 * Full logout: expire the server cookies, drop local state, then leave.
 * The redirect happens even if the request fails, so the user is never stranded
 * in a dashboard they can no longer use.
 */
export const logout = async (dispatch, redirectTo = '/login') => {
    try {
        await axios.post(`${API_URL}/app/logout`, {});
    } catch (error) {
        console.error('[session] Logout request failed:', error);
    } finally {
        clearLocalSession(dispatch);
        if (typeof window !== 'undefined') {
            window.location.href = redirectTo;
        }
    }
};
